import { CanvasRenderer, CanvasRenderingCtx } from '@damofire/uix-commons';
import { WarehouseSectionBounds } from '@damofire/data-watson';

export class SectionRenderer extends CanvasRenderer {
  private readonly color = 'rgba(255, 144, 22, 0.15)';
  private readonly borderColor = 'rgba(255, 144, 22, 0.5)';

  constructor(
    private readonly ctx: CanvasRenderingContext2D,
    private readonly sectionName: string,
    private readonly calculatedBounds: WarehouseSectionBounds,
    private readonly isHovered = false,
    private readonly systemsLabel?: string,
    private readonly aislesLabel?: string,
  ) {
    super();
  }

  draw(renderingCtx: CanvasRenderingCtx): void {
    if (!this.calculatedBounds) {
      return;
    }

    const { minX, minY, width, height, angle, cx, cy } = this.calculatedBounds;
    const hasObb = angle != null && cx != null && cy != null;

    const fillColor = this.isHovered ? 'rgba(255, 144, 22, 0.3)' : this.color;
    const strokeColor = this.isHovered ? 'rgba(255, 144, 22, 0.8)' : this.borderColor;

    // Draw boundary box — OBB (rotated) when backend provides angle+center, AABB otherwise
    this.ctx.save();
    this.ctx.beginPath();
    if (hasObb) {
      this.ctx.translate(cx, cy);
      this.ctx.rotate((angle * Math.PI) / 180);
      this.ctx.rect(-width / 2, -height / 2, width, height);
    } else {
      this.ctx.rect(minX, minY, width, height);
    }
    this.ctx.fillStyle = fillColor;
    this.ctx.fill();
    this.ctx.lineWidth = this.isHovered ? 3 : 2;
    this.ctx.strokeStyle = strokeColor;
    this.ctx.stroke();

    // Draw section name at center (scale font inversely to zoom for consistent size)
    this.ctx.save();
    if (!hasObb) {
      this.ctx.translate(minX + width / 2, minY + height / 2);
    }
    if (hasObb) {
      this.ctx.rotate(-(angle * Math.PI) / 180);
    }
    this.ctx.scale(1, -1);
    // Apply counter-rotation to keep text upright (renderingCtx.rx is in degrees)
    if (renderingCtx.rx) {
      this.ctx.rotate((-renderingCtx.rx * Math.PI) / 180);
    }
    // Scale font size inversely to zoom level so it stays readable
    const baseFontSize = 20;
    const maxFontSize = 80; // Cap the font size to prevent it from getting too large
    const calculatedSize = renderingCtx.sx > 0 ? baseFontSize / renderingCtx.sx : baseFontSize;
    const fontSize = Math.min(calculatedSize, maxFontSize);
    this.ctx.font = `bold ${fontSize}px Arial`;
    this.ctx.fillStyle = '#ff9016';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(this.sectionName, 0, 0);

    this.ctx.font = `${fontSize / 2}px Arial`;
    let textPos = fontSize;
    if (this.systemsLabel) {
      this.ctx.fillText(this.systemsLabel, 0, textPos);
      textPos += fontSize;
    }

    if (this.aislesLabel) {
      this.ctx.fillText(this.aislesLabel, 0, textPos);
    }

    this.ctx.restore();

    this.ctx.restore();
  }
}
